import { ArrowBigLeft } from 'lucide-react';
import * as React from 'react';

import { DataSpan } from '@/ux-common/components/data-span/data-span';
import { cn } from '@/ux-common/lib/utils';

/**
 * StagedValue component — old → new value-change lockup.
 *
 * Composes two {@link DataSpan} lockups separated by a left-pointing arrow to
 * show a value transitioning from a previous (`old`) value to a staged (`new`)
 * one — e.g. an input pending a change, or a metric whose value is being
 * edited. The new value+unit are highlighted (`.staged-value-new`), the old
 * value+unit are muted (`.staged-value-old`), and the arrow points at the new
 * value. The new/old coloring is owned here, not by DataSpan.
 *
 * Styling — including size — is a CSS-class concern, matching the rest of the
 * registry (DataSpan, Badge, …). There is no `size` prop: compose a
 * `staged-value-{size}` class onto `className` and the theme does the rest
 * (`staged-value-sm` / `-lg` / `-3xl`; default is the base, `-md` an alias).
 * The theme keys both the inner value font scale and the arrow glyph size off
 * that single root class — none of it lives in this file.
 *
 * `unit` renders on **both** values (state-colored to match each side),
 * mirroring the Figma spec and Gaia's value-change display. The arrow is
 * decorative (`aria-hidden`); pass an `aria-label` if the change needs an
 * accessible description.
 *
 * @example
 * ```tsx
 * <StagedValue newValue="75" oldValue="72" unit="°F" />
 *
 * // Compact — size is a class, not a prop
 * <StagedValue newValue="1.4" oldValue="1.2" unit="kW" className="staged-value-sm" />
 * ```
 */

export interface StagedValueProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** The staged (new) value, rendered highlighted. */
  newValue: React.ReactNode;
  /** The previous (old) value, rendered muted. */
  oldValue: React.ReactNode;
  /** Unit rendered on both values. Omit for unitless changes. */
  unit?: React.ReactNode;
}

export const StagedValue = React.forwardRef<HTMLSpanElement, StagedValueProps>(
  ({ className, newValue, oldValue, unit, ...props }, ref) => {
    return (
      <span
        ref={ref}
        data-component="StagedValue"
        className={cn('staged-value', className)}
        {...props}
      >
        <DataSpan className="staged-value-new">
          <span className="data-span-value">{newValue}</span>
          {unit ? <span className="data-span-unit">{unit}</span> : null}
        </DataSpan>
        <ArrowBigLeft className="staged-value-arrow" aria-hidden="true" />
        <DataSpan className="staged-value-old">
          <span className="data-span-value">{oldValue}</span>
          {unit ? <span className="data-span-unit">{unit}</span> : null}
        </DataSpan>
      </span>
    );
  }
);

StagedValue.displayName = 'StagedValue';
